import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

//MUI
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";

import FormikControl from "../../components/formik/FormikControl";
import Loader from "../../components/loader/Loader";

import UserHierarchyService from "../../services/UserHierarchyService";
import { Mode } from "./UserHierarchyPage";
import { UserHierarchyModel } from "../../models/UserHierarchyModel";
// import { statusOptions } from "../../utility/utils";
import { fetchUserHierarchys } from "../../redux/actions/UserHierarchyActions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { createAutocompleteOptions } from "../../utility/utils";
import ConfirmDialog from "../ConfirmDialog";

const validationSchema = Yup.object({
  bdpoCuserId: Yup.number(),
  bdheadUserId: Yup.number().min(1, "Required").required("Required"),
  sbuid: Yup.number().min(1, "Required").required("Required"),
  sbgid: Yup.number().min(1, "Required").required("Required"),
  csgoid: Yup.number().min(1, "Required").required("Required"),
  // status: Yup.string().required("Required"),
});

type FormProps = Yup.InferType<typeof validationSchema>;

type Props = {
  mode: Mode;
  handleModalClose: () => void;
  data?: UserHierarchyModel;
};

const UserHierarchyForm = ({ mode, handleModalClose, data }: Props) => {
  const initialValues = useMemo(
    () => ({
      bdpoCuserId: 0,
      bdheadUserId: 0,
      sbuid: 0,
      sbgid: 0,
      csgoid: 0,
      // status: "",
    }),
    []
  );

  const [formState, setFormState] = useState<FormProps>(initialValues);
  const dispatch = useAppDispatch();

  const displayToast = useAppSelector((state) => state.toast.displayToast);
  const allUsers = useAppSelector((state) => state.user.all_Users);
  const allSBUs = useAppSelector((state) => state.sbu.all_SBUs);
  const allSBGs = useAppSelector((state) => state.sbg.all_SBGs);
  const allCSGOs = useAppSelector((state) => state.csgo.all_CSGOs);

  const SBUOptions = useMemo(() => {
    return createAutocompleteOptions(allSBUs, "sbuid", "name");
  }, [allSBUs]);

  const userOptions = useMemo(() => {
    return createAutocompleteOptions(
      allUsers,
      "userId",
      "firstName",
      "lastName"
    );
  }, [allUsers]);

  const SBGOptions = useMemo(() => {
    return createAutocompleteOptions(allSBGs, "sbgid", "name");
  }, [allSBGs]);

  const CSGOOptions = useMemo(() => {
    return createAutocompleteOptions(allCSGOs, "csgoid", "name");
  }, [allCSGOs]);

  useEffect(() => {
    if (mode === "delete" && data) {
      setFormState(() => ({
        bdpoCuserId: data.bdpoCuserId,
        bdheadUserId: data.bdheadUserId,
        sbuid: data.sbuid,
        sbgid: data.sbgid,
        csgoid: data.csgoid,
        // status: data.isActive ? "active" : "inactive",
      }));
    }
  }, [mode, data]);

  const submitHandler = async (
    values: FormProps,
    actions: FormikHelpers<FormProps>
  ) => {
    try {
      let formData: UserHierarchyModel = {
        bdpoCuserId: 0,
        bdheadUserId: 0,
        sbuid: 0,
        sbgid: 0,
        csgoid: 0,
        isActive: true,
        createdDate: new Date(),
      };
      if (mode === "add") {
        formData.bdpoCuserId = values.bdpoCuserId || 0;
        formData.bdheadUserId = values.bdheadUserId;
        formData.sbuid = values.sbuid;
        formData.sbgid = values.sbgid;
        formData.csgoid = values.csgoid;
        // formData.isActive = values.status === "active" ? true : false;
      } else if (data) {
        formData = {
          ...data,
          bdpoCuserId: data.bdpoCuserId,
          bdheadUserId: data.bdheadUserId,
          sbuid: data.sbuid,
          sbgid: data.sbgid,
          csgoid: data.csgoid,
          // isActive: values.status === "active" ? true : false,
        };
      }
      mode === "delete"
        ? await UserHierarchyService.deleteUserHierarchy(formData)
        : await UserHierarchyService.createUserHierarchy(formData);
      actions.setSubmitting(false);
      handleModalClose();
      dispatch(fetchUserHierarchys());
      displayToast(
        `User hierarchy ${mode === "add" ? "created" : "deleted"} successfully`,
        "success"
      );
    } catch (error) {
      actions.setSubmitting(false);
    }
  };
  return (
    <Formik
      initialValues={formState}
      validationSchema={validationSchema}
      onSubmit={submitHandler}
      enableReinitialize={true}
    >
      {(formik) => (
        <>
          <Form>
            {mode === "delete" ? (
              <ConfirmDialog handleModalClose={handleModalClose} />
            ) : (
              <Stack gap={2}>
                <FormikControl
                  control="autocomplete"
                  options={userOptions}
                  name="bdpoCuserId"
                  size="small"
                  label="BDPO User"
                  placeholder="Select BDPO User"
                />
                <FormikControl
                  control="autocomplete"
                  options={userOptions}
                  name="bdheadUserId"
                  size="small"
                  label="BD Head User"
                  placeholder="Select BD Head User"
                />
                <FormikControl
                  control="autocomplete"
                  options={SBUOptions}
                  name="sbuid"
                  size="small"
                  label="SBU"
                  placeholder="Select SBU"
                />
                <FormikControl
                  control="autocomplete"
                  options={SBGOptions}
                  name="sbgid"
                  size="small"
                  label="SBG"
                  placeholder="Select SBG"
                />
                <FormikControl
                  control="autocomplete"
                  options={CSGOOptions}
                  name="csgoid"
                  size="small"
                  label="CSGO"
                  placeholder="Select CSGO"
                />
                {/* <FormikControl
                  control="autocomplete"
                  options={statusOptions}
                  name="status"
                  size="small"
                  label="Status"
                  placeholder="Select Status"
                /> */}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                >
                  Submit
                </Button>
              </Stack>
            )}
          </Form>
          {formik.isSubmitting && <Loader />}
        </>
      )}
    </Formik>
  );
};

export default UserHierarchyForm;
